import React, { useCallback, useEffect, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import { connect, useSelector } from 'react-redux'
import { useDebounce } from 'react-use'
import Scrollbars from 'react-custom-scrollbars'
import { requestCreateReservation } from '../../../actions/bookingCalendar/reservationModal/toggleReservationModal'
import { isTouchDevice } from '../../../components/MediaQuery'
import { IStore } from '../../../reducers'
import { getCalculatedSummary } from '../../../reducers/bookingCalendar/selectors/daySummary'
import { IRoomWithEvents } from '../../../reducers/bookingCalendar/selectors/rooms'
import { getUserSummary } from '../../../reducers/userObject/selectors/settings'
import { determineGridPosition } from '../../../utils/Canvas'
import CalendarCanvas from '../../Canvas/CalendarCanvas'
import { findEventByRoomDay } from './CanvasHelpers'
import EventHint from './EventHint'
import { PopoveredIcon2 } from '../../../components/PopoveredIcon/PopoveredIcon'
import { isLicenseValid } from '../../../reducers/userAccount/selectors/license'
import UpgradePremiumBlockScreenModal from '../../UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { isUserNewPackage } from '../../../reducers/userAccount/selectors/userAccount'

const CalendarCanvasWrapper = ({
  width,
  height,
  constants,
  rooms,
  startRoomIdx,
  scrollTop,
  scrollLeft,
  startDay,
  style,
  selectedDays,
  scrollHeight,
  scrollWidth,
  scrollbarsRef,
  dpConstants,
  onScroll,
  toggleDragging,
  toggleIsScrolling,
  toggleEvent,
  summary,
  summarySettings,
  minDate,
}) => {
  const [wrapperRef, setWrapperRef] = useState(null)
  const [wrapperRect, setWrapperRect] = useState(null)
  const [hoveredEvent, setHoveredEvent] = useState(null)
  const [eventHintOpen, setEventHintOpen] = useState('')
  const [eventHintPosition, setEventHintPosition] = useState({ x: 0, y: 0 })
  const [isScrolling, setIsScrolling] = useState(false)
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)
  const premium = useSelector(isLicenseValid)
  const isLicenseActive = useSelector(isLicenseValid)
  const isNewPremiumPriceGroup = useSelector(isUserNewPackage)
  const isFreeAccountUser = isNewPremiumPriceGroup && !isLicenseActive

  const handlePopoverOpen = event => {
    if (!wrapperRect) return

    const div = document.createElement('div')
    div.style.position = 'fixed'
    div.style.top = `${event.clientY}px`
    div.style.left = `${event.clientX}px`
    div.style.width = '1px'
    div.style.height = '1px'
    div.style.background = 'red'
    document.body.appendChild(div)
    event.preventDefault()
    event.stopPropagation()
    if (isMobile) {
      setIsModalOpened(true)
    } else {
      setPopoverAnchor(div)
    }
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
    setIsModalOpened(false)
  }

  const closeEventHint = useCallback(() => {
    setEventHintOpen('')
  }, [])

  const getOffsets = () => ({
    xOffset: Math.round(scrollLeft % constants.GRID_WIDTH),
    yOffset: Math.round(scrollTop % constants.GRID_HEIGHT),
  })

  const [, cancelOpenEventHint] = useDebounce(
    () => {
      if (!hoveredEvent || isScrolling) {
        return setEventHintOpen('')
      }

      const { eventId, x, y } = hoveredEvent
      if (eventId && isLicenseActive) {
        if (eventId !== eventHintOpen) {
          setEventHintPosition({ x, y })
          setEventHintOpen(eventId)
        }
      } else {
        setHoveredEvent(false)
      }
    },
    800,
    [hoveredEvent, isScrolling],
  )

  const clearEvent = () => {
    cancelOpenEventHint()
    setEventHintOpen('')
    setHoveredEvent(null)
  }

  const isScrollingHandler = useCallback(
    toggle => () => {
      clearEvent()
      toggleIsScrolling(toggle)()
      setIsScrolling(toggle)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setIsScrolling, toggleIsScrolling],
  )

  useEffect(() => {
    if (!wrapperRef) {
      return
    }

    const syncWrapperRect = () => {
      setWrapperRect(wrapperRef.getBoundingClientRect())
    }

    syncWrapperRect()
    const observer = new ResizeObserver(syncWrapperRect)
    observer.observe(wrapperRef)

    return () => {
      observer.disconnect()
    }
  }, [wrapperRef, width, height])

  const hintRefObject = useCallback(() => {
    if (!wrapperRect || !eventHintOpen) {
      return {}
    }

    const h = Math.min(constants.GRID_HEIGHT, height - eventHintPosition.y)
    const { xOffset, yOffset } = getOffsets()
    const top =
      wrapperRect.top + eventHintPosition.y * constants.GRID_HEIGHT - yOffset
    const left =
      wrapperRect.left +
      eventHintPosition.x * constants.GRID_WIDTH +
      5 -
      xOffset
    return {
      clientHeight: constants.GRID_HEIGHT,
      clientWidth: constants.GRID_WIDTH,
      getBoundingClientRect: () => ({
        bottom: top + h,
        height: h,
        left,
        right: left + constants.GRID_WIDTH - 5,
        top,
        width: constants.GRID_WIDTH,
      }),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constants, eventHintOpen, eventHintPosition, wrapperRect])

  const mouseMoveListener = e => {
    if (!wrapperRef || isScrolling || isTouchDevice || !rooms.length) {
      return
    }

    const rect = wrapperRef.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    const mouseX = e.clientX - rect.left
    if (mouseX >= 0 && mouseY >= 0) {
      const { day, room, x, y } = determineGridPosition<IRoomWithEvents>(
        constants,
        startDay,
        startRoomIdx,
        scrollTop,
        scrollLeft,
        rooms,
        mouseX,
        mouseY,
        true,
      )
      if (day && room) {
        const eventId = findEventByRoomDay(room, day)

        if ('calendarCursor' in window) {
          ;(window as any).calendarCursor(x, y, day, room, eventId)
        }

        if (!(eventId && hoveredEvent && hoveredEvent.eventId === eventId)) {
          cancelOpenEventHint()
          setEventHintOpen('')
        }

        if (!hoveredEvent || (hoveredEvent.x !== x || hoveredEvent.y !== y)) {
          setHoveredEvent({
            day,
            eventId,
            room,
            x,
            y,
          })
        }
      } else {
        setHoveredEvent(null)
      }
    }
  }

  const clickListener = e => {
    if (!wrapperRef || !rooms.length) {
      return
    }

    const rect = wrapperRef.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    const mouseX = e.clientX - rect.left
    if (mouseX >= 0 && mouseY >= 0) {

      const { day, room } = determineGridPosition(
        constants,
        startDay,
        startRoomIdx,
        scrollTop,
        scrollLeft,
        rooms,
        mouseX,
        mouseY,
        true,
      )
      if (day && room) {
        if (!isFreeAccountUser) {
          const eventId = findEventByRoomDay(room as any, day)
          toggleEvent(room.id, day, eventId)
        } else {
          handlePopoverOpen(e)
        }
      }
    }
  }

  const mouseLeaveListener = () => {
    clearEvent()
  }

  return (
    <>
      <div
        style={{
          cursor: 'pointer',
          ...style,
        }}
        ref={setWrapperRef}
      >
        <div
          style={{
            height: `${height}px`,
            width: `${width}px`,
            zIndex: 1,
          }}
        >
          <Scrollbars
            onScrollStart={isScrollingHandler(true)}
            onScrollStop={isScrollingHandler(false)}
            onScroll={onScroll}
            onDragStart={toggleDragging(true)}
            onDragEnd={toggleDragging(false)}
            style={{ zIndex: 53 }}
            ref={scrollbarsRef}
            className={'fakescrollbars'}
          >
            <div
              style={{
                fontFamily: 'Montserrat, sans-serif',
                height: `${scrollHeight}px`,
                position: 'relative',
                width: `${scrollWidth}px`,
              }}
              onMouseMove={mouseMoveListener}
              onMouseLeave={mouseLeaveListener}
              onClick={clickListener}
            >
              <div
                style={{
                  height: `${height}px`,
                  width: `${width}px`,
                }}
              />
              <EventHint
                open={eventHintOpen}
                close={closeEventHint}
                anchorEvent={hintRefObject}
                eventId={
                  hoveredEvent && hoveredEvent.eventId
                    ? hoveredEvent.eventId
                    : null
                }
              />
            </div>
          </Scrollbars>
        </div>
        <div
          style={{
            height,
            left: 0,
            position: 'absolute',
            top: 0,
            width,
            zIndex: 1,
          }}
        >
          <CalendarCanvas
            width={width}
            height={height}
            constants={constants}
            dpConstants={dpConstants}
            rooms={rooms}
            startRoomIdx={startRoomIdx}
            scrollTop={scrollTop}
            scrollLeft={scrollLeft}
            startDay={startDay}
            selectedDays={selectedDays}
            hoveredEvent={hoveredEvent}
            summary={summary}
            summarySettings={summarySettings}
            minDate={minDate}
          />
        </div>
        {!premium && (
          <PopoveredIcon2
            anchorEl={popoverAnchor}
            onClose={handlePopoverClose}
            withIcon={false}
          />
        )}
        {isMobile && (
          <UpgradePremiumBlockScreenModal
            open={isModalOpened}
            close={() => setIsModalOpened(false)}
          />
        )}
      </div>
    </>
  )
}

const mapStateToProps = (state: IStore, ownProps) => {
  return {
    summary: ownProps.withSummary ? getCalculatedSummary(state) : null,
    summarySettings: getUserSummary(state),
  }
}

const mapDispatchToProps = dispatch => ({
  toggleEvent: (roomId, from, eventId) =>
    dispatch(requestCreateReservation(roomId, from, eventId)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CalendarCanvasWrapper)
